/**
 * ClientUsersSection — sección USUARIOS del panel Administrar Cliente.
 *
 * Reconstruida con el catálogo de componentes reusables:
 *   ConfigSectionShell + tab "Listado" → ListingHeader + <DataHealthList> (listado unificado con
 *   resize/separadores/paginación top+bottom) + navegación por teclado (useListKeyboardNav) +
 *   click fila → <ItemDetailDrawer> montado como OVERLAY sobre la tabla (DataHealthList no soporta
 *   fila expandible intercalada), con EDIT en el MISMO cajón (sin abrir otro modal). "Reset
 *   password" sigue como ícono llave → PasswordResetLinkModal (link, no edición).
 *
 * Reemplaza el render inline de UsersModal en Config→Usuarios. UsersModal SIGUE usándose como
 * modal desde ClientCard, por eso no se toca.
 *
 * Ver: docs/design-system/COMPONENT_CATALOG.md
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Users, List, User, Mail, Shield, Calendar, KeyRound, Edit, Trash2,
  Save, X, Building2, Check, ChevronDown,
} from 'lucide-react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import axios from 'axios';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import ConfigSectionShell, { SectionTab } from '../common/ConfigSectionShell';
import ListingHeader from '../common/ListingHeader';
import useListKeyboardNav from '../common/useListKeyboardNav';
import { ItemDetailDrawer } from '../common/ItemDetail';
import DataHealthList, { type DataHealthColumn } from './dataHealth/DataHealthList';
import PasswordResetLinkModal from './PasswordResetLinkModal';

interface UserRow {
  uid: string;
  email: string;
  name: string;
  lastname: string;
  user_type: 'user' | 'admin' | 'superadmin';
  assigned_clients?: string[];
  created_at: any;
}

interface ClientOption {
  uid: string;
  name: string;
  display_name?: string;
}

interface Props {
  client: { uid: string; name: string; display_name?: string };
}

const TABS: SectionTab[] = [
  {
    id: 'listado',
    label: 'Listado',
    icon: List,
    description:
      'Usuarios con acceso a este cliente. Click en una fila para ver el detalle. Como superadmin podés editar, resetear contraseña o eliminar.',
  },
];

const typeColor = (type: string) =>
  type === 'superadmin'
    ? 'bg-purple-100 text-purple-800'
    : type === 'admin'
    ? 'bg-blue-100 text-blue-800'
    : 'bg-gray-100 text-gray-800';

const typeLabel = (type: string) =>
  type === 'superadmin' ? 'Super Admin' : type === 'admin' ? 'Administrador' : 'Usuario';

const fmtDate = (created_at: any): string =>
  created_at?.toDate
    ? created_at.toDate().toLocaleDateString('es-AR')
    : created_at?.seconds
    ? new Date(created_at.seconds * 1000).toLocaleDateString('es-AR')
    : 'N/A';

const ClientUsersSection: React.FC<Props> = ({ client }) => {
  const { userData, currentUser } = useAuth();
  const isSuperadmin = userData?.user_type === 'superadmin';

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'superadmin' | 'admin' | 'user'>('all');

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [detailOpen, setDetailOpen] = useState(false);

  // Orden controlado por DataHealthList (onSort). Los usuarios salen de Firestore
  // (no hay endpoint de listado con sort), así que el orden se resuelve local acá
  // — el padre sigue siendo el dueño del sort, como pide el contrato de DataHealthList.
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);

  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);

  // Edición inline dentro del cajón (modo 1: sin modal aparte).
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<{
    name: string; lastname: string;
    user_type: 'user' | 'admin' | 'superadmin';
    assigned_clients: string[];
  }>({ name: '', lastname: '', user_type: 'user', assigned_clients: [] });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [clientsDropdownOpen, setClientsDropdownOpen] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      // Dos queries mergeadas: (1) usuarios asignados explícitamente a este cliente,
      // (2) TODOS los superadmins — tienen acceso implícito a todos los clientes aunque
      // no estén en `assigned_clients`, así que deben aparecer en el listado de cualquier
      // cliente. Se dedupean por uid (un superadmin también puede estar asignado explícito).
      const [assignedSnap, superSnap] = await Promise.all([
        getDocs(query(usersRef, where('assigned_clients', 'array-contains', client.uid))),
        getDocs(query(usersRef, where('user_type', '==', 'superadmin'))),
      ]);
      const byUid = new Map<string, UserRow>();
      const collect = (snapshot: typeof assignedSnap) => {
        snapshot.forEach((docSnap) => {
          const d = docSnap.data();
          byUid.set(docSnap.id, {
            uid: docSnap.id,
            email: d.email || '',
            name: d.name || '',
            lastname: d.lastname || '',
            user_type: d.user_type || 'user',
            assigned_clients: d.assigned_clients || d.clients || (d.client ? [d.client] : []),
            created_at: d.created_at,
          });
        });
      };
      collect(assignedSnap);
      collect(superSnap);
      setUsers(Array.from(byUid.values()));
    } catch (e) {
      console.error('Error fetching users:', e);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [client.uid]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Lista de clientes para el multi-select de edición (una sola vez).
  // Se pre-carga en mount (efecto abajo) para poder resolver los nombres de las
  // pills "Clientes Asignados" también en la vista read-only del cajón.
  const fetchClientOptions = useCallback(async () => {
    if (clientOptions.length > 0) return;
    try {
      const snap = await getDocs(collection(db, 'clients'));
      const opts: ClientOption[] = [];
      snap.forEach((docSnap) => {
        const d = docSnap.data();
        if (d.active !== false) {
          opts.push({ uid: docSnap.id, name: d.name, display_name: d.display_name || d.name });
        }
      });
      setClientOptions(opts.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      console.error('Error fetching clients:', e);
    }
  }, [clientOptions.length]);

  // Pre-carga de clientes en mount → nombres de pills disponibles en read-only.
  useEffect(() => {
    fetchClientOptions();
  }, [fetchClientOptions]);

  // Filtrado + orden. Orden por defecto (sin sort explícito): superadmin > admin >
  // user, luego por nombre. Si el usuario clickea un header (sortBy/sortDir), se
  // ordena por esa columna.
  const filteredUsers = useMemo(() => {
    const typeOrder = { superadmin: 0, admin: 1, user: 2 } as const;
    const search = searchTerm.trim().toLowerCase();
    const filtered = users.filter((u) => {
      if (filterType !== 'all' && u.user_type !== filterType) return false;
      if (search) {
        const full = `${u.name} ${u.lastname}`.toLowerCase();
        return full.includes(search) || u.email.toLowerCase().includes(search);
      }
      return true;
    });

    const dir = sortDir === 'desc' ? -1 : 1;
    const keyVal = (u: UserRow): string | number => {
      switch (sortBy) {
        case 'usuario':
          return `${u.name} ${u.lastname}`.toLowerCase();
        case 'email':
          return u.email.toLowerCase();
        case 'tipo':
          return typeOrder[u.user_type];
        case 'creado':
          return u.created_at?.seconds ?? (u.created_at?.toDate ? u.created_at.toDate().getTime() / 1000 : 0);
        default:
          return '';
      }
    };

    if (sortBy) {
      return filtered.sort((a, b) => {
        const va = keyVal(a);
        const vb = keyVal(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }

    return filtered.sort((a, b) => {
      const t = typeOrder[a.user_type] - typeOrder[b.user_type];
      if (t !== 0) return t;
      return `${a.name} ${a.lastname}`.toLowerCase().localeCompare(`${b.name} ${b.lastname}`.toLowerCase());
    });
  }, [users, filterType, searchTerm, sortBy, sortDir]);

  // Reset de paginación/selección cuando cambian los filtros o el orden.
  useEffect(() => {
    setPageIndex(0);
    setSelectedIndex(-1);
    setDetailOpen(false);
    setEditMode(false);
  }, [filterType, searchTerm, sortBy, sortDir]);

  // Cambio de orden (click en header de DataHealthList). Volver a página 1.
  const handleSort = useCallback((by: string, dir: 'asc' | 'desc') => {
    setSortBy(by);
    setSortDir(dir);
    setPageIndex(0);
  }, []);

  const totalRows = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageStart = pageIndex * pageSize;
  const pageRows = filteredUsers.slice(pageStart, pageStart + pageSize);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setEditMode(false);
    setClientsDropdownOpen(false);
  }, []);

  // Selección global (índice dentro de filteredUsers) + ajuste de página si se sale.
  const selectIndex = useCallback(
    (i: number) => {
      if (totalRows === 0) return;
      const clamped = Math.max(0, Math.min(totalRows - 1, i));
      setSelectedIndex(clamped);
      setPageIndex(Math.floor(clamped / pageSize));
      // Al moverse a otro item, salimos de edición para no arrastrar el form.
      setEditMode(false);
    },
    [totalRows, pageSize]
  );

  const openDetail = useCallback((i: number) => {
    // Toggle: click en la fila ya abierta la cierra. Se computa fuera de los
    // updaters de setState (deben ser puros; StrictMode los invoca 2x en dev).
    if (selectedIndex === i && detailOpen) {
      setDetailOpen(false);
      setEditMode(false);
    } else {
      setSelectedIndex(i);
      setDetailOpen(true);
      setEditMode(false);
    }
  }, [selectedIndex, detailOpen]);

  useListKeyboardNav({
    count: totalRows,
    selectedIndex,
    onChange: selectIndex,
    onActivate: (i) => { setSelectedIndex(i); setDetailOpen(true); setEditMode(false); },
    // Mientras se edita en el cajón, no capturar ↑/↓ (son para el form/inputs).
    enabled: !editMode && !resetTarget,
  });

  const selectedUser = selectedIndex >= 0 ? filteredUsers[selectedIndex] : null;

  // Columnas para DataHealthList — MISMOS headers/orden/renders/anchos aprox. que la <table> ad-hoc.
  // (Usuario · Email · Tipo · Creado · Acciones). Anchos fijos salvo Usuario/Email (flexibles).
  const columns: DataHealthColumn<UserRow>[] = [
    {
      key: 'usuario',
      header: 'Usuario',
      render: (u) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-gray-600" />
          </div>
          <span className="font-medium text-gray-900 whitespace-nowrap">
            {u.name} {u.lastname}
          </span>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (u) => <span className="text-gray-600 whitespace-nowrap">{u.email}</span>,
    },
    {
      key: 'tipo',
      header: 'Tipo',
      width: '10rem',
      render: (u) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${typeColor(u.user_type)}`}>
          <Shield className="w-3 h-3" />
          {typeLabel(u.user_type)}
        </span>
      ),
    },
    {
      key: 'creado',
      header: 'Creado',
      width: '7rem',
      render: (u) => (
        <span className="text-gray-500 tabular-nums whitespace-nowrap">{fmtDate(u.created_at)}</span>
      ),
    },
    {
      key: 'acciones',
      header: 'Acciones',
      width: '6rem',
      align: 'right',
      render: (u) => (
        <div className="flex items-center justify-end gap-1.5">
          {isSuperadmin && (
            <button
              onClick={(e) => { e.stopPropagation(); openDetail(filteredUsers.indexOf(u)); }}
              className="p-1.5 rounded text-gray-500 border border-gray-200 hover:bg-gray-100 transition-colors"
              title="Ver detalle"
            >
              <List className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) return;
    try {
      const token = await currentUser?.getIdToken();
      await axios.delete(`/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeDetail();
      await fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(error?.response?.data?.error || 'Error al eliminar el usuario');
    }
  };

  const startEdit = (u: UserRow) => {
    setEditForm({
      name: u.name || '',
      lastname: u.lastname || '',
      user_type: u.user_type || 'user',
      assigned_clients: u.assigned_clients || [],
    });
    setEditError('');
    setEditMode(true);
    fetchClientOptions();
  };

  const toggleEditClient = (clientId: string) => {
    setEditForm((prev) => ({
      ...prev,
      assigned_clients: prev.assigned_clients.includes(clientId)
        ? prev.assigned_clients.filter((id) => id !== clientId)
        : [...prev.assigned_clients, clientId],
    }));
  };

  const saveEdit = async () => {
    if (!selectedUser) return;
    setEditError('');
    setSavingEdit(true);
    try {
      await updateDoc(doc(db, 'users', selectedUser.uid), {
        ...editForm,
        updated_at: new Date(),
      });
      await fetchUsers();
      setEditMode(false);
      setClientsDropdownOpen(false);
    } catch (e) {
      console.error('Error updating user:', e);
      setEditError('Error al actualizar el usuario');
    } finally {
      setSavingEdit(false);
    }
  };

  // Acciones del header del cajón (solo superadmin).
  const drawerActions = (u: UserRow) => {
    if (!isSuperadmin || editMode) return null;
    return (
      <>
        <button
          onClick={() => startEdit(u)}
          className="p-1.5 rounded text-blue-600 border border-blue-200 hover:bg-blue-50 hover:text-blue-700 transition-colors"
          title="Editar usuario"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={() => setResetTarget(u)}
          className="p-1.5 rounded text-amber-700 border border-amber-300 hover:bg-amber-50 transition-colors"
          title="Generar link de reset de contraseña"
        >
          <KeyRound className="w-4 h-4" />
        </button>
        <button
          onClick={() => handleDeleteUser(u.uid)}
          className="p-1.5 rounded text-red-600 border border-red-200 hover:bg-red-50 hover:text-red-700 transition-colors"
          title="Eliminar usuario"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </>
    );
  };

  const renderDrawerContent = (u: UserRow) => {
    if (editMode) {
      return (
        <div className="space-y-3">
          {editError && (
            <div className="p-2.5 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
              {editError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Mail className="w-3.5 h-3.5 inline mr-1" /> Email
              </label>
              <input
                type="email"
                value={u.email}
                disabled
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Shield className="w-3.5 h-3.5 inline mr-1" /> Tipo de Usuario
              </label>
              <select
                value={editForm.user_type}
                onChange={(e) =>
                  setEditForm({ ...editForm, user_type: e.target.value as UserRow['user_type'] })
                }
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="user">Usuario</option>
                <option value="admin">Administrador</option>
                <option value="superadmin">Super Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <User className="w-3.5 h-3.5 inline mr-1" /> Nombre
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <User className="w-3.5 h-3.5 inline mr-1" /> Apellido
              </label>
              <input
                type="text"
                value={editForm.lastname}
                onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Clientes asignados (multi-select) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <Building2 className="w-3.5 h-3.5 inline mr-1" /> Clientes Asignados
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setClientsDropdownOpen((v) => !v)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-left flex items-center justify-between focus:ring-2 focus:ring-blue-500"
              >
                <span className="text-gray-700">
                  {editForm.assigned_clients.length === 0
                    ? 'Seleccionar clientes...'
                    : `${editForm.assigned_clients.length} cliente${editForm.assigned_clients.length > 1 ? 's' : ''} seleccionado${editForm.assigned_clients.length > 1 ? 's' : ''}`}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${clientsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {clientsDropdownOpen && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                  {clientOptions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">Cargando clientes...</div>
                  ) : (
                    clientOptions.map((c) => (
                      <div
                        key={c.uid}
                        onClick={() => toggleEditClient(c.uid)}
                        className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer flex items-center justify-between text-sm text-gray-700"
                      >
                        <span>{c.display_name || c.name}</span>
                        {editForm.assigned_clients.includes(c.uid) && <Check className="w-4 h-4 text-blue-600" />}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {editForm.assigned_clients.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {editForm.assigned_clients.map((cid) => {
                  const c = clientOptions.find((o) => o.uid === cid);
                  return (
                    <span key={cid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {c?.display_name || c?.name || cid}
                      <button type="button" onClick={() => toggleEditClient(cid)} className="hover:bg-blue-200 rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setEditMode(false); setClientsDropdownOpen(false); }}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={savingEdit}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {savingEdit ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      );
    }

    // Vista de solo lectura.
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 text-gray-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">{u.name} {u.lastname}</h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 ${typeColor(u.user_type)}`}>
              <Shield className="w-3 h-3" />
              {typeLabel(u.user_type)}
            </span>
          </div>
        </div>

        {/* 3 campos core condensados en una fila (1/3 c/u). */}
        <dl className="grid grid-cols-3 gap-3">
          <div className="border border-gray-200 rounded-lg p-3 min-w-0">
            <dt className="text-xs text-gray-400 flex items-center gap-1.5 mb-1">
              <Mail className="w-3.5 h-3.5" /> Email
            </dt>
            <dd className="text-sm text-gray-900 break-all">{u.email}</dd>
          </div>
          <div className="border border-gray-200 rounded-lg p-3 min-w-0">
            <dt className="text-xs text-gray-400 flex items-center gap-1.5 mb-1">
              <Calendar className="w-3.5 h-3.5" /> Creado
            </dt>
            <dd className="text-sm text-gray-900 tabular-nums">{fmtDate(u.created_at)}</dd>
          </div>
          <div className="border border-gray-200 rounded-lg p-3 min-w-0">
            <dt className="text-xs text-gray-400 flex items-center gap-1.5 mb-1">
              <Shield className="w-3.5 h-3.5" /> UID
            </dt>
            <dd className="text-xs font-mono text-gray-500 break-all">{u.uid}</dd>
          </div>
        </dl>

        {/* Clientes asignados (read-only, mismo diseño de pills que en edición). */}
        <div>
          <h5 className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" />
            Clientes Asignados
            {u.user_type !== 'superadmin' && ` (${(u.assigned_clients || []).length})`}
          </h5>
          {u.user_type === 'superadmin' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">
              <Shield className="w-3 h-3" /> Todos los clientes
            </span>
          ) : u.assigned_clients && u.assigned_clients.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {u.assigned_clients.map((cid) => {
                const c = clientOptions.find((o) => o.uid === cid);
                return (
                  <span
                    key={cid}
                    className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {c?.display_name || c?.name || cid}
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">Sin clientes asignados</span>
          )}
        </div>

        {!isSuperadmin && (
          <p className="text-xs text-gray-400 italic">
            Las acciones de edición y borrado requieren rol superadmin.
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <ConfigSectionShell
        icon={Users}
        title="Usuarios"
        clientName={client.display_name || client.name}
        tabs={TABS}
        activeTab="listado"
        onTabChange={() => { /* única tab por ahora */ }}
      >
        <ListingHeader
          count={totalRows}
          total={users.length}
          itemNoun="usuario"
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: 'Buscar por nombre o email...',
          }}
          actions={
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as typeof filterType)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos los tipos</option>
              <option value="superadmin">Super Admin</option>
              <option value="admin">Administrador</option>
              <option value="user">Usuario</option>
            </select>
          }
        />

        {/* Listado unificado (DataHealthList): resize/separadores + paginación top+bottom propia.
            El detalle (ItemDetailDrawer) se monta como OVERLAY superpuesto sobre la tabla, porque
            DataHealthList no soporta fila expandible intercalada. */}
        <DataHealthList<UserRow>
          columns={columns}
          rows={pageRows}
          getRowId={(u) => u.uid}
          onRowClick={(u) => openDetail(filteredUsers.indexOf(u))}
          activeRowId={detailOpen && selectedUser ? selectedUser.uid : null}
          listId="config-usuarios"
          onSort={handleSort}
          sortBy={sortBy}
          sortDir={sortDir}
          loading={loading}
          emptyState={
            <div className="py-2 text-gray-500">
              <User className="mx-auto h-12 w-12 text-gray-400 mb-2" />
              <h3 className="text-sm font-medium text-gray-900">
                {users.length > 0 ? 'No se encontraron coincidencias' : 'No hay usuarios'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {users.length > 0
                  ? 'Intentá con otros términos de búsqueda o filtros'
                  : 'No hay usuarios asignados a este cliente'}
              </p>
            </div>
          }
          pagination={{
            idPrefix: 'users-listado',
            pageIndex,
            pageSize,
            totalRows,
            totalPages,
            onPageChange: setPageIndex,
            onPageSizeChange: (s) => {
              setPageSize(s);
              setPageIndex(0);
            },
          }}
          overlay={
            detailOpen && selectedUser ? (
              <div className="absolute inset-0 z-20 flex justify-end bg-black bg-opacity-10">
                <div className="w-[65%] border-l border-gray-200 bg-white shadow-xl overflow-y-auto p-2">
                  <ItemDetailDrawer
                    title={`${selectedUser.name} ${selectedUser.lastname}`}
                    subtitle={selectedUser.email}
                    icon={User}
                    headerActions={drawerActions(selectedUser)}
                    onPrev={() => selectIndex(selectedIndex - 1)}
                    onNext={() => selectIndex(selectedIndex + 1)}
                    hasPrev={selectedIndex > 0}
                    hasNext={selectedIndex >= 0 && selectedIndex < totalRows - 1}
                    onClose={closeDetail}
                  >
                    {renderDrawerContent(selectedUser)}
                  </ItemDetailDrawer>
                </div>
              </div>
            ) : null
          }
        />
      </ConfigSectionShell>

      {/* Modal reusable de reset (genera link, no edita el usuario). */}
      <PasswordResetLinkModal user={resetTarget} onClose={() => setResetTarget(null)} />
    </>
  );
};

export default ClientUsersSection;
